import PublicIcon from '@mui/icons-material/Public';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    getContrastRatio,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import pLimit from 'p-limit';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import colors from '/@/components/theme-provider/colors';
import useUnitProvider from '/@/core/context/unit';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { getWeatherForecast } from '/@/hooks/frost-risk';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { getColor } from '../gdu-forecast';

const limit = pLimit(5);

const locationsToSeries = async (filteredLocations, series, system) => {
    const newSeries = { ...series };
    // eslint-disable-next-line no-restricted-syntax
    for (const { location, lat, lng, index } of filteredLocations) {
        // eslint-disable-next-line no-await-in-loop
        if (!(location in series)) {
            const weatherData = await limit(() => getWeatherForecast(lat, lng, system));
            newSeries[location] = {
                name: location,
                data: weatherData.data.forecast.slice(1).map((dayForecast) => ({
                    date: _({
                        en: dayForecast.date.split('-').slice(-2).join('/'),
                        es: dayForecast.date.split('-').slice(-2).toReversed().join('/'),
                        pt: dayForecast.date.split('-').slice(-2).toReversed().join('/'),
                    }),
                    minTemp: Math.min(dayForecast.data?.[0]?.day.minTemp, dayForecast.data?.[0]?.night.minTemp),
                })),
                index,
            };
        }
    }
    return newSeries;
};

const MinTempForecastOverlay = ({ setIsLoading, isLoading, isHome }) => {
    const country = useSelector(getSessionCountry);
    const [series, setSeries] = useState({});
    const [locationName, setLocationName] = useState('');
    const debouncedLocationName = useDebounce(locationName, 300);

    type LocationOption = {
        location: string;
        id: string;
        lat: number;
        lng: number;
        index: number;
    };

    const [options, setOptions] = useState<LocationOption[]>([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<LocationOption[]>([]);

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!debouncedLocationName || debouncedLocationName.length <= 1) {
            setOptions([]);
            setOptionsLoading(false);
            return;
        }

        if (typeof google === 'undefined' || !google.maps) {
            setOptions([]);
            setOptionsLoading(false);
            return;
        }

        let active = true;
        setOptionsLoading(true);

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
            {
                address: debouncedLocationName,
                region: country?.code,
                componentRestrictions: { country: country?.code },
            },
            (results, status) => {
                if (!active) return;

                if (status === 'OK' && results) {
                    const mapped: LocationOption[] = results
                        .slice(0, 5)
                        .filter((r) => !!r.geometry && !!r.geometry.location)
                        .map((r, index) => ({
                            location: r.formatted_address,
                            id: r.place_id || `${index}`,
                            lat: r.geometry!.location!.lat(),
                            lng: r.geometry!.location!.lng(),
                            index,
                        }));
                    setOptions(mapped);
                } else {
                    setOptions([]);
                }

                setOptionsLoading(false);
            }
        );

        return () => {
            active = false;
        };
    }, [country?.code, debouncedLocationName]);
    const units = useUnitProvider();

    useEffect(() => {
        (async () => {
            const selected = selectedLocations || [];
            if (selected.length === 0) {
                setSeries({});
                return;
            }
            setIsLoading(true);
            const newSeries = await locationsToSeries(selected, series, units.system);
            setSeries(newSeries);
            setIsLoading(false);
        })();
    }, [selectedLocations]);
    const filteredSeries = useMemo(() => {
        const names = new Set((selectedLocations || []).map((l) => l.location));
        return Object.values(series).filter((s) => names.has(s.name));
    }, [series, selectedLocations]);

    return (
        <Box
            sx={{
                m: isHome ? 0 : 2,
                pb: 4,
                mx: {
                    xs: isHome ? 0 : 4,
                    sm: isHome ? 0 : 6,
                    md: isHome ? 0 : 8,
                },
                pointerEvents: 'auto',
            }}
        >
            <Typography variant='h5' fontWeight='bold' color='primary'>
                {_('Pronóstico de temperaturas mínimas')}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    my: 1,
                    mt: 3,
                }}
            >
                <PublicIcon fontSize='large' sx={{ mr: 1 }} color='primary' />
                <Typography variant='h6' color='primary'>
                    {_('Localidades')}
                </Typography>
            </Box>

            <Box sx={{ my: 2 }}>
                <Typography variant='body1' color='primary'>
                    {_('Selecciona cuál quieres ver en el gráfico')}
                </Typography>
            </Box>

            <Autocomplete
                multiple
                id='tags-outlined'
                options={options}
                getOptionLabel={(option) => option.location ?? option}
                filterSelectedOptions
                inputValue={locationName}
                noOptionsText={
                    debouncedLocationName.trim().length === 0
                        ? _('Escribí el nombre de una localidad')
                        : _('No hay localidades que coincidan con la búsqueda')
                }
                loading={optionsLoading}
                loadingText={_('Cargando')}
                onInputChange={(event, newInputValue, reason) => {
                    if (reason !== 'reset') setLocationName(newInputValue);
                }}
                isOptionEqualToValue={(option, value) => option.location === value.location}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={_('Localidades')}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <Fragment>
                                    {optionsLoading ? <CircularProgress color='inherit' size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </Fragment>
                            ),
                        }}
                    />
                )}
                value={selectedLocations || []}
                onChange={(event, value, reason, details) => {
                    if (reason === 'selectOption' && details?.option?.location) {
                        if (locationName) setLocationName('');
                        setSelectedLocations((prev) => {
                            const list = prev || [];
                            const exists = list.some((l) => l.location === details.option.location);
                            if (exists) return list;
                            const nextIndex = list.length;
                            return [...list, { ...details.option, index: nextIndex }];
                        });
                        return;
                    }
                    if (reason === 'removeOption' && details?.option?.location) {
                        setSelectedLocations((prev) => {
                            const filtered = (prev || []).filter((l) => l.location !== details.option.location);
                            // Reindex so colors stay compact and consistent
                            return filtered.map((l, idx) => ({ ...l, index: idx }));
                        });
                        return;
                    }
                    if (reason === 'clear') {
                        setSelectedLocations([]);
                    }
                }}
                renderTags={(values) => {
                    return (values || []).map(({ location, index }) => {
                        const color = getColor(index) as string;
                        return (
                            <Chip
                                key={index}
                                label={location}
                                onDelete={() =>
                                    setSelectedLocations((prev) => (prev || []).filter((l) => l.location !== location))
                                }
                                sx={{
                                    bgcolor: color,
                                    color: getContrastRatio(color, '#000000') > 4.5 ? '#000000' : '#ffffff',
                                    '& .MuiChip-label': {
                                        fontSize: 10,
                                    },
                                }}
                            />
                        );
                    });
                }}
            />

            {selectedLocations.length > 0 && (
                <Box
                    sx={{
                        width: { xs: '100%', md: isHome ? '100%' : '80%' },
                        height: isHome ? 250 : 400,
                        mt: 2,
                        mb: isHome ? 0 : 10,
                        mx: 'auto',
                    }}
                >
                    <ResponsiveContainer>
                        <LineChart data={series}>
                            <XAxis
                                dataKey='date'
                                type='category'
                                allowDuplicatedCategory={false}
                                label={{
                                    value: _('Fecha'),
                                    position: 'insideBottomRight',
                                    offset: 0,
                                    style: {
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: colors.gray.default,
                                    },
                                }}
                            />
                            <YAxis
                                dataKey='minTemp'
                                label={{
                                    value: _(`Temperatura Mínima (${units.temperature})`),
                                    position: 'insideLeft',
                                    angle: -90,
                                    offset: 0,
                                    style: {
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        color: colors.gray.default,
                                    },
                                }}
                                tickFormatter={(el) => `${el}${!isMobile ? `${units.temperature}` : ''}`}
                            />
                            <CartesianGrid stroke='#eee' strokeDasharray='1' />
                            <Tooltip formatter={(value) => `${value}${units.temperature}`} />
                            <Legend />
                            {filteredSeries.map((s) => (
                                <Line
                                    dataKey='minTemp'
                                    data={s.data}
                                    name={s.name}
                                    key={s.name}
                                    stroke={getColor(s.index)}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>

                    <Box sx={{ mt: 1, textAlign: 'center' }}>
                        <Typography variant='body2' color='primary.mui.inactive'>
                            {_('Fuente: The Weather Company')}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default MinTempForecastOverlay;
